import { createDebug } from '@/utils/debug'
import { isPptOnlineAddin } from '@/services/office/ppt-online-binding'

// AHA-48826: the PowerPoint Online slide binding, keyed by the add-in INSTANCE (the shape's
// `<we:webextension id>`, read from the frame name — see readOwnAddinInstanceId).
//
// WHY A THIRD STORE. Every other web store (ppt-online-binding, ppt-online-current-slide,
// ppt-show-owner-claim) is scoped by Office.context.document.url. PowerPoint Online pre-creates the
// Slide Show frames when a deck opens (`#cross-slide-preactived-addins`) and the show uses them —
// and in those frames document.url is EMPTY. So in the ordinary customer flow (open a saved deck,
// present) every document-scoped key was null, every lookup missed, and every AhaSlides slide showed
// the presentation list. Those frames have no slide token in their name either, and their
// getSelectedDataAsync is unreliable (see ppt-online-current-slide). What they DO have is the
// instance id, identical in the edit-view frame and the show frame of the same shape.
//
// So: written in the edit session under the frame's own instance id, read in the show under the
// same one.
//
// COPIES OF A DECK. The instance id and the slide token are saved in the .pptx, so a copy of the file
// (Save As, a duplicated file) carries the same ones. Nothing else readable in a show frame tells the
// two files apart either: the Rich API fails there, and every in-file id (tags, custom XML, settings)
// is copied too (measured 2026-10-15). Only the file's location differs. So every record also notes
// the document.url it was written under. Writes happen in the edit view and on insert, where the URL
// is present. The read uses it only to choose between files, never to find a record at all:
//   - a frame WITH a URL uses its own file's records;
//   - a frame WITHOUT one (the pre-created show frames above) uses every file's records while they
//     agree, and reports `ambiguous` when they do not, i.e. a copy rebound differently. The
//     presentation list is better than the other file's slide.
// A renamed or moved file looks like a copy, so a rebind after a move is ambiguous in a URL-less frame.
//
// ONE INSTANCE, SEVERAL SLIDES. "Add N slides" used to clone the host slide verbatim, and a user can
// still duplicate a slide by hand: every copy keeps the original's instance id. Such a key cannot
// say which slide a show frame is on, and silently picking one would show the WRONG slide, which is
// worse than the presentation list. So the value records every slide seen under the instance, keyed
// by the slide's persistent token (the part of its Rich API id after '#'), and a lookup that finds
// more than one distinct binding reports `ambiguous` rather than guessing. The insert path now gives
// every clone a fresh instance id (replaceAddinInstanceId), so new decks never get here.
//
// Toggle logging with: localStorage.debug = 'aha:ppt:instance-binding' (or 'aha:ppt:*').
const log = createDebug('ppt:instance-binding')

const KEY_PREFIX = 'aha:ppt:instance-slide:'
const DOCUMENT_LABEL = '| document'

const keyFor = (instanceId) => (instanceId ? `${KEY_PREFIX}${String(instanceId).toUpperCase()}` : null)

const currentDocumentUrl = () => window.Office?.context?.document?.url || null

// One record per slide token per document. A record written without a URL (none should be, but the
// store must not drop it) keeps the bare token as its slot.
const slotFor = (slideToken, documentUrl) => (documentUrl ? `${slideToken} ${documentUrl}` : String(slideToken))

// A new instance id, as `{GUID}` in the upper-case form PowerPoint itself writes. Lives here rather
// than beside replaceAddinInstanceId so the insert path can call it without pulling the lazily loaded
// zip patcher into the main bundle.
export const generateAddinInstanceId = () => {
  const uuid = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      // eslint-disable-next-line no-bitwise
      const random = (Math.random() * 16) | 0
      // eslint-disable-next-line no-bitwise
      return (char === 'x' ? random : ((random & 0x3) | 0x8)).toString(16)
    })
  return `{${uuid.toUpperCase()}}`
}

// "257#1147092600" -> "1147092600". The persistent half of a Rich API slide id: the same token the
// edit-view frame name carries (readOwnSlideTokenFromFrameName).
export const toSlideToken = (richSlideId) => {
  const parts = String(richSlideId || '').split('#')
  return parts.length > 1 && parts[1] ? parts[1] : null
}

const readEntry = (key) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || 'null')
    return parsed && parsed.slides && typeof parsed.slides === 'object' ? parsed : { slides: {} }
  } catch (error) {
    log('unparsable entry -> treating as empty |', key, error)
    return { slides: {} }
  }
}

const writeEntry = (key, entry) => {
  try {
    if (!Object.keys(entry.slides).length) {
      localStorage.removeItem(key)
      return
    }
    localStorage.setItem(key, JSON.stringify(entry))
  } catch (error) {
    log('write failed |', key, error)
  }
}

// A record is either one slide ({presentationId, slideId}) or a whole presentation
// ({activePresentationId}), the two shapes dataSlidePPT takes, or a file's unbind ({cleared}).
const bindingKey = (record) => {
  if (record.cleared) return 'cleared'
  return record.activePresentationId
    ? `presentation:${record.activePresentationId}`
    : `slide:${record.presentationId}:${record.slideId}`
}
const sameBinding = (a, b) => bindingKey(a) === bindingKey(b)

const toRecord = (value) => {
  if (value && value.presentationId && value.slideId) {
    return { presentationId: String(value.presentationId), slideId: String(value.slideId) }
  }
  if (value && value.activePresentationId) {
    return { activePresentationId: String(value.activePresentationId) }
  }
  return null
}

const toBinding = (record) => (record.activePresentationId
  ? { activePresentationId: record.activePresentationId }
  : { presentationId: record.presentationId, slideId: record.slideId })

// Records `value` — {presentationId, slideId}, or {activePresentationId} for a whole presentation —
// as the binding of the slide `slideToken` under `instanceId`. Anything else (an unbind) marks that
// slide cleared for this file, so the show never resolves a binding the frame no longer holds.
// `commonSlideId` is optional: kept from the previous record when the caller does not know it.
export const savePptInstanceBinding = (instanceId, slideToken, value, { commonSlideId = null } = {}) => {
  if (!isPptOnlineAddin()) return
  const key = keyFor(instanceId)
  if (!key || !slideToken) {
    log('save skipped | instance =', instanceId, '| slide token =', slideToken)
    return
  }
  const entry = readEntry(key)
  const token = String(slideToken)
  const documentUrl = currentDocumentUrl()
  const slot = slotFor(token, documentUrl)
  const record = toRecord(value)
  if (!record) {
    // A marker rather than a delete, so the other file's record cannot stand in for this one: a frame
    // with this URL then finds no binding, and a URL-less frame sees the files disagree (ambiguous).
    // The common id stays on it so a lookup matched by common id sees the disagreement too.
    if (documentUrl) {
      entry.slides[slot] = {
        cleared: true,
        commonSlideId: (entry.slides[slot] && entry.slides[slot].commonSlideId) || null,
        documentUrl,
      }
    } else {
      delete entry.slides[slot]
    }
    writeEntry(key, entry)
    log('cleared |', instanceId, '| slide', token, DOCUMENT_LABEL, documentUrl)
    return
  }
  const previous = entry.slides[slot] || {}
  entry.slides[slot] = {
    ...record,
    commonSlideId: commonSlideId !== null && commonSlideId !== undefined
      ? String(commonSlideId)
      : (previous.commonSlideId || null),
    documentUrl,
  }
  writeEntry(key, entry)
  log('saved |', instanceId, '| slide', token, DOCUMENT_LABEL, documentUrl, '=>', entry.slides[slot])
}

const distinctBindings = (records) => records
  .filter((record, index) => records.findIndex((other) => sameBinding(other, record)) === index)

// The records this frame may resolve from: its own file's when it knows its URL and that file has any
// (a record written without a URL counts as its own), otherwise all of them. See COPIES OF A DECK.
const candidateRecords = (records, documentUrl) => {
  if (!documentUrl) return records
  const own = records.filter((record) => !record.documentUrl || record.documentUrl === documentUrl)
  return own.length ? own : records
}

// Returns { binding, ambiguous }. `binding` is { presentationId, slideId } (or { activePresentationId })
// when the instance resolves to exactly one AhaSlides slide, or to one of several matched by
// `commonSlideId` (the Common-API id of the shown slide, when the caller has it). Otherwise binding is
// null, and `ambiguous` says whether that was because the instance is shared by slides, or by copies of
// the deck, bound differently.
export const loadPptInstanceBinding = (instanceId, { commonSlideId = null } = {}) => {
  if (!isPptOnlineAddin()) return { binding: null, ambiguous: false }
  const key = keyFor(instanceId)
  if (!key) return { binding: null, ambiguous: false }
  const documentUrl = currentDocumentUrl()
  const records = candidateRecords(Object.values(readEntry(key).slides), documentUrl)
  const distinct = distinctBindings(records)
  if (!distinct.length) {
    log('MISS |', instanceId)
    return { binding: null, ambiguous: false }
  }
  if (distinct.length === 1) {
    if (distinct[0].cleared) {
      log('MISS (cleared) |', instanceId, DOCUMENT_LABEL, documentUrl)
      return { binding: null, ambiguous: false }
    }
    const binding = toBinding(distinct[0])
    log('HIT |', instanceId, DOCUMENT_LABEL, documentUrl, '=>', binding)
    return { binding, ambiguous: false }
  }
  // A copy of the deck carries the slide's common id too, so the match has to be unanimous as well.
  const matched = commonSlideId !== null && commonSlideId !== undefined
    ? distinctBindings(records.filter((record) => record.commonSlideId
      && String(record.commonSlideId) === String(commonSlideId)))
    : []
  if (matched.length === 1 && !matched[0].cleared) {
    log('HIT (shared instance, matched by common id', commonSlideId, ') |', instanceId, '=>', toBinding(matched[0]))
    return { binding: toBinding(matched[0]), ambiguous: false }
  }
  log('AMBIGUOUS |', instanceId, 'is shared by', distinct.length, 'differently bound slides | common id =',
    commonSlideId, DOCUMENT_LABEL, documentUrl)
  return { binding: null, ambiguous: true }
}

// The show-side remap (a present-run reset moves the AhaSlides slide id) folded back into every
// record that still holds the pre-remap pair, so the next cold load resolves the moved slide directly.
export const foldRemapIntoInstanceBinding = (instanceId, binding, remapped) => {
  if (!isPptOnlineAddin()) return
  const key = keyFor(instanceId)
  if (!key || !binding || !remapped || binding.activePresentationId || sameBinding(binding, remapped)) return
  const entry = readEntry(key)
  let changed = false
  Object.keys(entry.slides).forEach((token) => {
    if (sameBinding(entry.slides[token], binding)) {
      entry.slides[token] = {
        ...entry.slides[token],
        presentationId: String(remapped.presentationId),
        slideId: String(remapped.slideId),
      }
      changed = true
    }
  })
  if (changed) {
    writeEntry(key, entry)
    log('remap folded in |', instanceId, '|', binding.slideId, '->', remapped.slideId)
  }
}
